import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Lang = 'en' | 'ar';
type Dir = 'ltr' | 'rtl';

interface I18nCtx {
  language: Lang;
  setLanguage: (l: Lang) => void;
  direction: Dir;
}

const Ctx = createContext<I18nCtx>({ language: 'en', setLanguage: () => void 0, direction: 'ltr' });

const LANG_KEY = 'cmp.portal.lang';
const LANG_MANUAL_KEY = 'cmp.portal.lang.manual';

export function PortalI18nProvider({ children }: { children?: React.ReactNode }) {
  const [manual, setManual] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LANG_MANUAL_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [language, setLanguageState] = useState<Lang>(() => {
    try {
      const stored = localStorage.getItem(LANG_KEY) as Lang | null;
      if (stored === 'ar' || stored === 'en') return stored;
      // Auto-detect from system preferences on first load
      const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
      const langs = (nav.languages || [nav.language || 'en']).map((l) => String(l).toLowerCase());
      const isArabic = langs.some((l) => l.startsWith('ar'));
      return isArabic ? 'ar' : 'en';
    } catch {
      return 'en';
    }
  });

  // Persist selections and manual/auto mode
  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, language);
      localStorage.setItem(LANG_MANUAL_KEY, manual ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [language, manual]);

  // Follow system language changes when not manually overridden
  useEffect(() => {
    if (manual) return;
    const handler = () => {
      try {
        const nav = navigator as Navigator;
        const langs = (nav.languages || [nav.language || 'en']).map((l) => String(l).toLowerCase());
        const isArabic = langs.some((l) => l.startsWith('ar'));
        setLanguageState(isArabic ? 'ar' : 'en');
      } catch {
        /* noop */
      }
    };
    window.addEventListener('languagechange', handler);
    return () => window.removeEventListener('languagechange', handler);
  }, [manual]);

  const setLanguage = (l: Lang) => {
    setManual(true);
    setLanguageState(l);
  };

  const value = useMemo<I18nCtx>(
    () => ({ language, setLanguage, direction: language === 'ar' ? 'rtl' : 'ltr' }),
    [language],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  return useContext(Ctx);
}
